"use client"

import { useEffect, useState, type ReactNode } from "react"
import Link from "next/link"
import { Menu, X } from "lucide-react"

import useSiteHeaderHeight from "@packages/web/hooks/use-site-header-height"
import { Button } from "@packages/web/components/ui/button"
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@packages/web/components/ui/sidebar"
import { cn } from "@packages/web/components/ui/utils"

import AppSidebar from "./AppSidebar"
import BackofficeBrand from "./BackofficeBrand"

function MobileHeader() {
  const { openMobile, toggleSidebar } = useSidebar()

  return (
    <header
      className={cn(
        // Stay above the sheet (z-50) for the whole open/close animation —
        // do not drop z-index when openMobile flips to false mid-slide.
        "sticky top-0 z-[60] flex h-14 items-center gap-2 border-b border-border/60 bg-white/95 px-3 backdrop-blur md:hidden",
      )}
    >
      <Link
        href="/"
        aria-label="Ernest backoffice — accueil"
        className="min-w-0"
      >
        <BackofficeBrand iconSize={28} />
      </Link>
      <Button
        aria-label={openMobile ? "Fermer le menu" : "Ouvrir le menu"}
        aria-expanded={openMobile}
        className="ml-auto size-9 text-ink"
        size="icon"
        type="button"
        variant="ghost"
        onClick={toggleSidebar}
      >
        {openMobile ? (
          <X className="size-5" aria-hidden />
        ) : (
          <Menu className="size-5" aria-hidden />
        )}
      </Button>
    </header>
  )
}

function useDashboardHeaderHeight() {
  const [height, setHeight] = useState("0px")

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const sync = () => {
      setHeight(mq.matches ? "3.5rem" : "0px")
    }

    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  return height
}

export default function DashboardShell({ children }: { children: ReactNode }) {
  useSiteHeaderHeight(useDashboardHeaderHeight())

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="max-h-svh min-h-0 min-w-0 overflow-hidden bg-canvas">
        <MobileHeader />
        <div className="flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col overflow-hidden bg-canvas">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
